import { Box, CircularProgress, Grid, TextField } from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import { Button } from 'components/facets'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import { useUser } from 'components/providers/UserProvider'
import exportImg from 'img/export-2.png'
import {
  createCsvReport,
  downloadCsvReport,
  fetchCsvReports,
  fetchSubscriptionGaps,
  getStatusCsvReport,
} from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table, TableRowRenderer } from 'react-virtualized'
import {
  computeSubscriptionGaps,
  CsvReportHistory,
  formatDateToYYYYMMDD,
  getNDaysAgo,
  MonthlySubscription,
  normalizeCsvReportHistory,
  parseLocalDate,
  SubscriptionGap,
  TAXATION_RELEASE_DATE,
  toEndOfInterval,
} from 'shared'
import './exports-presenter.css'

export const TaxationExports = (): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()
  const createdAtRaw = (user as any).created_at ?? user.createdAt
  const rawCreatedAt = createdAtRaw ? new Date(createdAtRaw) : null
  const accountCreatedAt = rawCreatedAt && !isNaN(rawCreatedAt.getTime()) ? rawCreatedAt : null
  const effectiveMinDate =
    accountCreatedAt && accountCreatedAt > TAXATION_RELEASE_DATE
      ? accountCreatedAt
      : TAXATION_RELEASE_DATE
  const defaultFrom = getNDaysAgo(30)
  const [from, setFrom] = useState<Date>(
    defaultFrom < effectiveMinDate ? effectiveMinDate : defaultFrom
  )
  const [to, setTo] = useState<Date>(new Date())
  const { enqueueSnackbar } = useSnackbar()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLastPage, setIsLastPage] = useState<boolean>(true)

  const [gaps, setGaps] = useState<SubscriptionGap[]>([])

  const [downloads, setDownloads] = useState<CsvReportHistory[]>([])
  const [loadingReports, setLoadingReports] = useState(true)
  const [filterMonth, setFilterMonth] = useState<Date | null>(null)
  const [hasRecords, setHasRecords] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const handleFromChange = (date: Date) => {
    setFrom(date)
    setHasSearched(true)
  }
  const handleToChange = (date: Date) => {
    setTo(date)
    setHasSearched(true)
  }

  const today = new Date()
  const todayStr = formatDateToYYYYMMDD(today)
  const minDateStr = formatDateToYYYYMMDD(effectiveMinDate)

  // Default: show gaps closed within the last 24 months.
  // When the user modifies from/to, switch to showing only gaps overlapping the selected range.
  const visibleGaps = useMemo(() => {
    if (hasSearched) {
      const fromMs = from.getTime()
      const toMs = toEndOfInterval(to).getTime()
      return gaps.filter((g) => {
        const gFromMs = new Date(g.from).getTime()
        const gToMs = new Date(g.to).getTime()
        return gFromMs <= toMs && gToMs >= fromMs
      })
    }
    const twoYearsAgo = new Date()
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2)
    const twoYearsAgoMs = twoYearsAgo.getTime()
    return gaps.filter((g) => new Date(g.to).getTime() >= twoYearsAgoMs)
  }, [gaps, hasSearched, from, to])

  const handleCreateCsv = async () => {
    if (from > today || to > today) {
      enqueueSnackbar(t('exports.errors.errorFutureDate'), { variant: 'error' })
      return
    }
    setIsCreating(true)
    try {
      const exportJob = await createCsvReport(from.toISOString(), toEndOfInterval(to).toISOString())
      const normalize = normalizeCsvReportHistory(exportJob)

      pollExportStatus(normalize.id)

      setDownloads((prev) => [normalize, ...prev])
      setHasRecords(true)
    } catch {
      enqueueSnackbar(t('exports.errors.errorCreateExport'), { variant: 'error' })
    } finally {
      setIsCreating(false)
    }
  }

  const handleFetchReports = async (overrideMonth?: Date | null) => {
    setLoadingReports(true)
    const month = overrideMonth !== undefined ? overrideMonth : filterMonth
    try {
      let createdAtFrom: string | undefined
      let createdAtTo: string | undefined
      if (month) {
        const year = month.getFullYear()
        const m = month.getMonth() + 1
        const monthStr = `${year}-${String(m).padStart(2, '0')}`
        createdAtFrom = `${monthStr}-01`
        const lastDay = new Date(year, m, 0).getDate()
        createdAtTo = `${monthStr}-${String(lastDay).padStart(2, '0')}`
      }
      const { files, hasNextPage } = await fetchCsvReports(currentPage, createdAtFrom, createdAtTo)
      if (files) {
        const data: CsvReportHistory[] = []
        files.forEach((item: any) => data.push(normalizeCsvReportHistory(item)))
        setDownloads(files)
        setIsLastPage(!hasNextPage)
        if (!month && files.length > 0) setHasRecords(true)
      } else {
        enqueueSnackbar(t('taxation.errors.errorFetchReports'), { variant: 'error' })
      }
    } finally {
      setLoadingReports(false)
    }
  }

  const pollExportStatus = async (id: number) => {
    let res
    try {
      res = await getStatusCsvReport(id)
    } catch {
      enqueueSnackbar(t('taxation.errors.errorCheckExportStatus'), { variant: 'error' })
      return
    }

    if (res.status === 'ready') {
      setDownloads((prev) =>
        prev.map((exp) => (exp.id === id ? { ...exp, exportEndedAt: new Date() } : exp))
      )
      enqueueSnackbar(t('taxation.csvReady'), { variant: 'success' })
      handleFetchReports()
      return
    }

    if (res.status === 'pending') {
      // try again in 5 seconds
      setTimeout(() => pollExportStatus(id), 5000)
      return
    }

    // status === 'error'
    enqueueSnackbar(t('taxation.errors.errorCheckExportStatus'), { variant: 'error' })
  }

  const fetchGaps = async () => {
    try {
      const res = await fetchSubscriptionGaps()
      const monthlySubscriptions: MonthlySubscription[] = res.map((item) => {
        return {
          subscriptionStart: item.subscriptionStart,
          subscriptionEnd: item.subscriptionEnd,
        } as MonthlySubscription
      })
      const gaps = computeSubscriptionGaps(monthlySubscriptions)
      setGaps(gaps)
    } catch (e) {
      enqueueSnackbar(t('taxation.errors.errorSubscriptionGaps'), { variant: 'error' })
    }
  }

  useEffect(() => {
    fetchGaps()
    handleFetchReports()
  }, [currentPage])

  const handleApplyFilters = () => {
    if (currentPage === 1) {
      handleFetchReports()
    } else {
      setCurrentPage(1)
    }
  }

  const handleResetFilter = () => {
    setFilterMonth(null)
    if (currentPage === 1) {
      handleFetchReports(null)
    } else {
      setCurrentPage(1)
    }
  }

  const handleDownloadCsv = async (id: number) => {
    let res
    try {
      res = await downloadCsvReport(id)
    } catch {
      enqueueSnackbar(t('taxation.errors.errorDownloadCsv'), { variant: 'error' })
      return
    }

    const blob = new Blob([res.data], { type: 'text/csv' })

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `report-${id}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const rowRenderer: TableRowRenderer = ({ key, style, columns, className, rowData }) => {
    rowData

    return (
      <div key={key} style={style} className={`${className}`}>
        {columns}
      </div>
    )
  }

  return (
    <div className="tab-page-content">
      {loadingReports ? (
        <div className="w-100 d-flex flex-column align-items-center mt-5">
          <CircularProgress size={60} />
        </div>
      ) : !hasRecords ? (
        <div className="w-100 d-flex flex-column align-items-center mt-3">
          <img src={exportImg} style={{ maxWidth: 600, width: '100%', height: 'auto' }} />
          <Box maxWidth={750} width="100%" px={2} className="text-center">
            <h2 className="fst-italic mt-3 text-center">{t('exports.taxationDescription')}</h2>
            <Grid
              container
              spacing={4}
              alignItems="center"
              justifyContent="center"
              className="mt-2"
            >
              <Grid item xs={12} sm={6} md={4} lg={2} xl={4}>
                <TextField
                  fullWidth
                  label={t('orders.from')}
                  type="date"
                  defaultValue={formatDateToYYYYMMDD(from)}
                  onChange={(e) => handleFromChange(parseLocalDate(e.target.value))}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{ min: minDateStr, max: todayStr }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2} xl={4}>
                <TextField
                  fullWidth
                  label={t('orders.to')}
                  type="date"
                  defaultValue={formatDateToYYYYMMDD(to)}
                  onChange={(e) => handleToChange(parseLocalDate(e.target.value))}
                  inputProps={{ min: minDateStr, max: todayStr }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
                <Button variant="contained" onClick={handleCreateCsv} disabled={isCreating}>
                  {isCreating ? <CircularProgress size={20} /> : t('csv.export')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </div>
      ) : (
        <>
          <h2 className="mb-4">{t('exports.taxationTitle')}</h2>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span>{t('exports.generateNewFileFrom')}</span>
            <TextField
              type="date"
              size="small"
              defaultValue={formatDateToYYYYMMDD(from)}
              onChange={(e) => handleFromChange(parseLocalDate(e.target.value))}
              inputProps={{
                style: { padding: '4px 8px', fontSize: 13 },
                min: minDateStr,
                max: todayStr,
              }}
              style={{ width: 150 }}
            />
            <span>{t('exports.generateNewFileTo')}</span>
            <TextField
              type="date"
              size="small"
              defaultValue={formatDateToYYYYMMDD(to)}
              onChange={(e) => handleToChange(parseLocalDate(e.target.value))}
              inputProps={{
                style: { padding: '4px 8px', fontSize: 13 },
                min: minDateStr,
                max: todayStr,
              }}
              style={{ width: 150 }}
            />
            <Button variant="contained" onClick={handleCreateCsv} disabled={isCreating}>
              {isCreating ? <CircularProgress size={20} /> : t('csv.export')}
            </Button>
          </div>
          {accountCreatedAt && (
            <small className="text-muted">
              {t('exports.accountCreatedOn', {
                date: accountCreatedAt.toLocaleDateString(),
              })}
            </small>
          )}

          {visibleGaps.length > 0 && (
            <div className="warning-box mt-3" style={{ width: 'fit-content' }}>
              <div className="d-flex flex-row align-items-center mb-1">
                <i className="fa-light fa-triangle-exclamation me-2 fs-6" />
                <strong>{t('taxation.inactiveSubscription')}</strong>
              </div>
              <p className="p-0 m-0">{t('taxation.inactiveSubscriptionDescription')}</p>
              <div className="mt-2">
                {visibleGaps.map((gap) => (
                  <div key={gap.from}>
                    {t('orders.from')} <strong>{new Date(gap.from).toLocaleDateString()}</strong>{' '}
                    {t('orders.to')} <strong>{new Date(gap.to).toLocaleDateString()}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="d-flex align-items-center gap-2 flex-wrap mt-4 mb-3">
            <span>{t('exports.showExportsCovering')}</span>
            <DatePicker
              views={['year', 'month']}
              openTo="month"
              format="MM/yyyy"
              variant="inline"
              autoOk
              value={filterMonth}
              onChange={(date) => setFilterMonth(date)}
              maxDate={today}
              inputProps={{ style: { padding: '4px 8px', fontSize: 13 } }}
              style={{ width: 170 }}
              PopoverProps={{ classes: { paper: 'exports-month-picker' } }}
            />
            <Button variant="outlined" onClick={handleApplyFilters}>
              {t('exports.applyFilter')}
            </Button>
            {filterMonth && (
              <Button variant="outlined" onClick={handleResetFilter}>
                {t('exports.resetFilter')}
              </Button>
            )}
          </div>

          <div>
            <div className="responsive-table-container">
              <AutoSizer disableHeight>
                {({ width: autoWidth }) => {
                  const width = Math.max(autoWidth, 700)
                  return (
                    <Table
                      width={width}
                      height={40 + Math.max(40, downloads.length * 40)}
                      headerHeight={40}
                      rowHeight={40}
                      rowCount={downloads.length}
                      rowGetter={({ index }) => downloads[index]}
                      rowRenderer={rowRenderer}
                      overscanRowCount={5}
                    >
                      <Column
                        width={width * 0.25}
                        label={t('exports.fromDate')}
                        dataKey="from"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">
                            {new Date(a.intervalStartDate).toLocaleString()}
                          </div>
                        )}
                      />
                      <Column
                        width={width * 0.25}
                        label={t('exports.toDate')}
                        dataKey="to"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">
                            {new Date(a.intervalEndDate).toLocaleString()}
                          </div>
                        )}
                      />
                      <Column
                        width={width * 0.25}
                        label={t('exports.createdAt')}
                        dataKey="created"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">{new Date(a.createdAt).toLocaleString()}</div>
                        )}
                      />
                      <Column
                        width={width * 0.1}
                        label={t('exports.status')}
                        dataKey="status"
                        headerClassName="centered-header"
                        cellRenderer={({ rowData: a }) => (
                          <Box textAlign={'center'}>
                            {a.lastErrorAt ? (
                              <i className="fa-light fa-circle-x error-color" />
                            ) : a.exportEndedAt ? (
                              <i className="fa-light fa-circle-check" style={{ color: 'green' }} />
                            ) : (
                              <CircularProgress size={16} />
                            )}
                          </Box>
                        )}
                      />
                      <Column
                        width={width * 0.15}
                        label={t('exports.download')}
                        dataKey="download"
                        headerClassName="centered-header"
                        cellRenderer={({ rowData: a }) => (
                          <Box textAlign={'center'}>
                            {!a.lastErrorAt && a.exportEndedAt ? (
                              <i
                                className="fa-light fa-arrow-down-to-bracket table-action-icon"
                                onClick={() => handleDownloadCsv(a.id)}
                                style={{ cursor: 'pointer' }}
                              />
                            ) : (
                              '—'
                            )}
                          </Box>
                        )}
                      />
                    </Table>
                  )
                }}
              </AutoSizer>
            </div>
            {(currentPage > 1 || !isLastPage) && (
              <TablePaginationControls
                currentPage={currentPage}
                setPage={setCurrentPage}
                isLastPage={isLastPage}
                startFromZero={false}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
